import { DateTime } from "luxon";

const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Convert an ISO date string to a Luxon DateTime in the venue's timezone
 */
function toVenueTime(isoString, timezone) {
  return DateTime.fromISO(isoString, { zone: "utc" }).setZone(
    timezone || DEFAULT_TIMEZONE
  );
}

/**
 * The start time an occurrence should be listed and bucketed under.
 *
 * Open-ended offerings (private lessons, season passes) keep their original
 * `starts_at` — often years in the past — while remaining bookable because
 * `ends_at` is still in the future. Listing them by `starts_at` sorts them
 * ahead of everything else and files them under a month tab for a year that is
 * long gone, so anything already underway is treated as starting now.
 */
export function occurrenceStart(item) {
  const timezone = item.timezone || DEFAULT_TIMEZONE;
  const dt = toVenueTime(item.events[0].starts_at, timezone);
  const now = DateTime.now().setZone(timezone);
  return dt < now ? now : dt;
}

/**
 * Stable, chronologically sortable key for a month tab, e.g. "2026-09".
 * The year matters: keying on the month name alone merges Sep 2026 with
 * Sep 2027, and the day buttons built from that bucket then collide.
 */
export function monthKey(dt) {
  return dt.toFormat("yyyy-LL");
}

/**
 * Human label for a month key. The year is only shown when the calendar spans
 * more than one, keeping tabs short for the common single-year case.
 */
export function monthLabel(key, showYear) {
  const dt = DateTime.fromFormat(key, "yyyy-LL");
  return showYear ? dt.toFormat("LLL yy") : dt.toFormat("LLL");
}

/**
 * Flatten courses so each event occurrence becomes its own item.
 * Recurring courses (with multiple events) produce one entry per occurrence,
 * sorted chronologically. Each entry keeps all course fields but has a
 * single-element `events` array so the rest of the code can keep using events[0].
 */
export function flattenCourses(courses) {
  const flattened = [];
  (courses || []).forEach((course) => {
    if (course.events && course.events.length > 0) {
      course.events.forEach((event, index) => {
        flattened.push({
          ...course,
          _flatId: `${course.id}-${index}`,
          events: [event],
        });
      });
    }
  });
  flattened.sort(
    (a, b) => occurrenceStart(a).toMillis() - occurrenceStart(b).toMillis()
  );
  return flattened;
}

export function structureDataByMonth(data) {
  let structuredData = {};

  data.forEach((item) => {
    if (item.events && item.events.length > 0) {
      const key = monthKey(occurrenceStart(item));

      if (!structuredData[key]) {
        structuredData[key] = [];
      }

      structuredData[key].push(item);
    }
  });

  // Object key order drives the month tabs, so sort explicitly rather than
  // relying on the insertion order of the flattened list.
  return Object.keys(structuredData)
    .sort()
    .reduce((sorted, key) => {
      sorted[key] = structuredData[key];
      return sorted;
    }, {});
}

export function getEventsByMonth(monthKeyValue, structuredData) {
  let eventsForMonth = [];

  if (structuredData[monthKeyValue]) {
    structuredData[monthKeyValue].forEach((item) => {
      if (item.events && item.events.length > 0) {
        const dt = occurrenceStart(item);

        eventsForMonth.push({
          // Full calendar date, so days in the same month of different years
          // stay distinct.
          key: dt.toISODate(),
          date: dt.day,
          dayOfWeek: dt.toFormat("ccc"), // "Mon", "Tue", etc.
          id: item._flatId || item.id,
          month: monthKeyValue,
        });
      }
    });
  }

  return eventsForMonth;
}

export function getUniqueEventDays(events) {
  const seen = new Set();
  const uniqueDays = [];

  events.forEach((event) => {
    // Dedupe on the full date. Deduping on the day of the month alone dropped
    // every occurrence that shared a day number with an earlier one, which for
    // a weekly series spanning two years is roughly half of them.
    if (seen.has(event.key)) return;
    seen.add(event.key);
    uniqueDays.push({
      key: event.key,
      date: event.date,
      dayOfWeek: event.dayOfWeek,
      id: event.id,
      month: event.month,
    });
  });

  return uniqueDays;
}

export function getUniqueTags(data) {
  const uniqueTagNames = new Set();
  (data || []).forEach((item) => {
    (item.tags || []).forEach((tag) => {
      if (tag && typeof tag.name === "string" && tag.name.trim().length) {
        uniqueTagNames.add(tag.name);
      }
    });
  });
  return Array.from(uniqueTagNames).sort((a, b) => a.localeCompare(b));
}

export function filterCards(cards, searchText, activeTagsSet) {
  const query = (searchText || "").trim().toLowerCase();
  const hasActiveTags = activeTagsSet && activeTagsSet.size > 0;

  return (cards || []).filter((item) => {
    const name = (item.name || "").toLowerCase();
    const description = (item.description || "").toLowerCase();
    const tagNames = (item.tags || []).map((t) =>
      (t?.name || "").toLowerCase()
    );

    const textMatch =
      !query ||
      name.includes(query) ||
      description.includes(query) ||
      tagNames.some((t) => t.includes(query));

    const tagsMatch =
      !hasActiveTags || tagNames.some((t) => activeTagsSet.has(t));

    return textMatch && tagsMatch;
  });
}

function hexToRgb(hex) {
  // Remove the "#" if it's present
  hex = hex.replace(/^#/, "");

  // Parse the hex string into RGB components
  var bigint = parseInt(hex, 16);
  var r = (bigint >> 16) & 255;
  var g = (bigint >> 8) & 255;
  var b = bigint & 255;

  return [r, g, b];
}

function calculateLuminance([r, g, b]) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

export function isLightMode(hex1, hex2) {
  const luminance1 = calculateLuminance(hexToRgb(hex1));
  const luminance2 = calculateLuminance(hexToRgb(hex2));

  return luminance1 > luminance2;
}

export function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
/**
 * scrolling code for CaledarHeader - works, but buggy
 */
// useEffect(() => {
//   const handleScroll = () => {
//     const header = document.getElementById("calendar-header");
//     const headerHeight = header ? header.offsetHeight : 0;
//     const cards = document.querySelectorAll(".card");
//     cards.forEach((card) => {
//       const rect = card.getBoundingClientRect();

//       const isVisible =
//         rect.top >= headerHeight &&
//         rect.bottom <= headerHeight + rect.height * 2 + 24;

//       if (isVisible) {
//         const { month } = card.dataset;
//         // This card is fully visible below the header - update the state accordingly
//         const id = +card.id;
//         const monthlyEvents = getEventsByMonth(month, structuredData);
//         const eventDateObj = monthlyEvents.find((obj) => obj.id === id);
//         console.log(eventDateObj);
//         if (eventDateObj) {
//           setSelectedDate(eventDateObj);
//           setSelectedMonth(month);
//         }
//       }
//     });
//   };
//   const debouncedScrollHandler = debounce(handleScroll, 100);

//   window.addEventListener("scroll", debouncedScrollHandler);

//   return () => {
//     window.removeEventListener("scroll", debouncedScrollHandler);
//   };
// }, [selectedMonth, structuredData]);
